// src/com/sms_message/SmsMessage.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, smsMessageListRepo } from '../../value/AppModel';
import { LIST_SMS_MESSAGE, ADD_SMS_MESSAGE } from './SmsMessageGql';
import { SmsMessageDictionary } from './SmsMessageDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import DateRangePicker from '../../common/DateRangePicker';
import IsValid from '../../value/IsValid';
import ValueMisc from '../../value/ValueMisc';
import SmsMessageSender from './SmsMessageSender';

/**
 * (SMS_MESSAGE) 정보 View Controller
 * SMS 전송 기록
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-01-24 13:26:20 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function SmsMessage({
    viewServiceTags // for switching view
}) {
    const [duration, setDuration] = useState({dateFrom:'', dateTill:''});
    //const [fetchDuration, setFetchDuration] = useState({dateFrom:null, dateTill:null});
    //const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const smsMessageListData = useReactiveVar(smsMessageListRepo);
    const [searchText, setSearchText] = useState('');
    const [countToDisplay, setCountToDisplay] = useState(0);
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const fetchMemo = useRef({dateFrom:null, dateTill:null});

    // ##### Call GraphQL to get List #####
    const [getSmsMessageList, responseList] = useLazyQuery(LIST_SMS_MESSAGE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: SmsMessageDictionary.errorTitle.Get, message: [error.message]})
    });

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at con-sole (Uncaught exception) ###
    const [addSmsMessage, responseAdd] = useMutation( ADD_SMS_MESSAGE, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: SmsMessageDictionary.errorTitle.Add, message: [error.message]})
	} );

    const getSmsMessageListNow = (argObj) => {
        if(argObj.dateFrom && argObj.dateTill) {
            const param = {variables:argObj};
            getSmsMessageList(param);
            fetchMemo.current = {dateFrom:argObj.dateFrom, dateTill:argObj.dateTill};
        }
    };

    useEffect(()=>{
        if(IsValid.date(smsMessageListData.param.dateFrom) && IsValid.date(smsMessageListData.param.dateTill)) {
            if(fetchMemo.current.dateFrom !== smsMessageListData.param.dateFrom || fetchMemo.current.dateTill !== smsMessageListData.param.dateTill) {
                getSmsMessageListNow(smsMessageListData.param);
            }
        }
        else {
            const newData = {...smsMessageListData};
            newData.param = {
                dateFrom: ValueMisc.getYMD(),
                dateTill: ValueMisc.getYMD(),
            };
            smsMessageListRepo(newData);
        }
    }, [smsMessageListData]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.smsMessageList) {
            lastReloadRef.current = new Date().getTime()/1000;
            const newData = {...smsMessageListData};
            newData.records = data.smsMessageList;
            newData.when = lastReloadRef.current;
            smsMessageListRepo(newData);
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.smsMessageAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getSmsMessageListNow(smsMessageListData.param);
            enqueueSnackbar('문자 전송이 완료되었습니다.', {variant:'success', autoHideDuration:2000});
        }
        else addAlert({title:SmsMessageDictionary.errorTitle.Add, message:[data.smsMessageAdd.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getSmsMessageListNow(smsMessageListData.param);
    };

    const onRequestSave = (item) => {
        const mutateParam = {variables:{smsMessage:item}};
        addSmsMessage(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onChangeFrom = (ymd) => {
        setDuration({...duration, dateFrom:ymd});
        const newData = {...smsMessageListData};
        newData.param.dateFrom = ymd;
        smsMessageListRepo(newData);
    };

    const onChangeTo = (ymd) => {
        setDuration({...duration, dateTill:ymd});
        const newData = {...smsMessageListData};
        newData.param.dateTill = ymd;
        smsMessageListRepo(newData);
    };

    const canAdd = checkAuth(authTableTree.tree.pr_sms_message_add);

    const loadingAny
        = responseList.loading
        || responseAdd.loading;

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <DateRangePicker
                        dateFrom={duration.dateFrom}
                        dateTo={duration.dateTill}
                        onChangeFrom={onChangeFrom}
                        onChangeTo={onChangeTo}
                    />
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                        style={{marginLeft:20}}
                    />
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countToDisplay} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                            disabled={
                                loadingAny
                            }
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={SmsMessageDictionary.tableProps.tableId}
                tableProps={SmsMessageDictionary.tableProps}
                records={smsMessageListData.records}
                search={searchText}
                tools={[]}
                onChangeCountToDisplay={setCountToDisplay}
            />
            <SmsMessageSender
                open={editorState.open}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={responseAdd}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}
